import Model, { attr, belongsTo } from '@ember-data/model';
import { copyable } from 'eflex/decorators';
import { rollbackBelongsTo, isBelongsToRefDirty } from 'eflex/util/relationship-helpers';
import { validator, buildValidations } from '@eflexsystems/ember-tracked-validations';
import { rangeCorrectness } from 'eflex/util/validators';

@copyable
@buildValidations({
  lowerLimit: [
    validator('number', {
      allowString: true,
      allowBlank: true,
      get disabled() {
        return this.model.isVariableDefNotNumber;
      },
    }),
  ],

  upperLimit: [
    validator('number', {
      allowString: true,
      allowBlank: true,
      get disabled() {
        return this.model.isVariableDefNotNumber;
      },
    }),
    validator('inline', {
      validate(upperLimit, options, variableDefConfig) {
        return rangeCorrectness(
          variableDefConfig,
          variableDefConfig.lowerLimit,
          upperLimit,
          'plant.taskConfig.limitOverlapUpper',
        );
      },
      get disabled() {
        return this.model.isVariableDefNotNumber;
      },
    }),
  ],

  decimalLength: [
    validator('number', {
      allowString: true,
      allowBlank: false,
      integer: true,
      gte: 0,
      lte: 10,
      get disabled() {
        return this.model.isVariableDefNotNumber;
      },
    }),
  ],
})
class VariableDefConfig extends Model {
  @attr('boolean', { defaultValue: true }) enabled;
  @attr('boolean', { defaultValue: true }) required;
  @attr('number') upperLimit;
  @attr('number') lowerLimit;
  @attr('number', { defaultValue: 2 }) decimalLength;
  @attr value;

  @belongsTo('task-config', { async: false, inverse: 'variableDefConfigs' }) taskConfig;
  @belongsTo('variable-def', { async: false, inverse: 'variableDefConfigs' }) variableDef;
  @belongsTo('variable-def', { async: false, inverse: null }) variableDefForValue;

  get isDirty() {
    return super.isDirty || isBelongsToRefDirty(this, 'variableDefForValue');
  }

  get errorIdentifier() {
    return this.taskConfig?.errorIdentifier;
  }

  get selectedValue() {
    return this.variableDefForValue ?? this.formattedValue;
  }

  get isVariableDefNotNumber() {
    return this.variableDef?.type !== 'number';
  }

  get formattedValue() {
    const value = this.value;

    if (value == null) {
      return null;
    }

    return { name: this.value };
  }

  get copyableOptions() {
    return {
      ignoreAttributes: new Set(['variableDef', 'taskConfig']),
    };
  }

  rollbackAttributes() {
    rollbackBelongsTo(this, 'variableDefForValue');
    super.rollbackAttributes();
  }
}

export default VariableDefConfig;
